import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container, Box, Typography, makeStyles } from "@material-ui/core";
import { toggleTab } from "src/actions/HomeActions";
import DetailsPage from "src/components/hotel/DetailsEditTab";
import RoomsTab from "src/components/hotel/RoomsEditTab";
import ImagesTab from "src/components/hotel/ImagesEditTab";

const TOKENS = {
  surface: "#FFFFFF",
  border: "#EEF0F5",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  divider: "#F1F5F9",
};

const TABS = [
  { key: "details", label: "Property Details", number: 1 },
  { key: "rooms", label: "Rooms", number: 2 },
  { key: "images", label: "Images", number: 3 },
];

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: TOKENS.surface,
    minHeight: "100%",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(8),
  },
  pageHeader: {
    marginBottom: 24,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: TOKENS.textPrimary,
  },
  pageSubtitle: {
    fontSize: 14,
    color: TOKENS.textSecondary,
    marginTop: 2,
  },
  tabBar: {
    display: "flex",
    alignItems: "center",
    marginBottom: 28,
    padding: "8px 12px",
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 14,
    gap: 4,
  },
  tab: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flex: 1,
    padding: "8px 14px",
    borderRadius: 10,
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    border: "none",
    background: "none",
    "&:hover": {
      backgroundColor: TOKENS.divider,
    },
  },
  tabActive: {
    backgroundColor: `${TOKENS.accent}10`,
  },
  tabCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
  },
  tabCircleActive: {
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
  },
  tabCircleInactive: {
    backgroundColor: TOKENS.divider,
    color: TOKENS.textMuted,
  },
  tabLabel: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  tabLabelActive: { color: TOKENS.textPrimary },
  tabLabelInactive: { color: TOKENS.textMuted },
  tabDivider: {
    width: 1,
    height: 24,
    backgroundColor: TOKENS.border,
    flexShrink: 0,
  },
}));

function EditHotel() {
  const classes = useStyles();
  const tabValue = useSelector((state) => state.homes.edit_hotel_tab);
  const dispatch = useDispatch();

  return (
    <Container className={classes.root}>
      <Box className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>
          Edit Hotel Property
        </Typography>
        <Typography className={classes.pageSubtitle}>
          Update your property details, rooms, and images
        </Typography>
      </Box>

      {/* Tab bar */}
      <Box className={classes.tabBar}>
        {TABS.map((tab, idx) => {
          const isActive = tab.key === tabValue;
          return (
            <React.Fragment key={tab.key}>
              {idx > 0 && <Box className={classes.tabDivider} />}
              <Box
                component="button"
                className={`${classes.tab} ${isActive ? classes.tabActive : ""}`}
                onClick={() => dispatch(toggleTab(tab.key))}
              >
                <Box
                  className={`${classes.tabCircle} ${
                    isActive
                      ? classes.tabCircleActive
                      : classes.tabCircleInactive
                  }`}
                >
                  {tab.number}
                </Box>
                <Typography
                  className={`${classes.tabLabel} ${
                    isActive ? classes.tabLabelActive : classes.tabLabelInactive
                  }`}
                >
                  {tab.label}
                </Typography>
              </Box>
            </React.Fragment>
          );
        })}
      </Box>

      {tabValue === "details" && <DetailsPage />}
      {tabValue === "rooms" && <RoomsTab />}
      {tabValue === "images" && <ImagesTab />}
    </Container>
  );
}

export default EditHotel;
